import { Box, Checkbox } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { stepModeEnum } from '../../constants';
import {
    useCreateDailyTempWorkerMutation,
    useDailyTempWorkerMutation,
    useDeleteDailyTempWorkerMutation,
} from '../../hooks/mutations/useDailyTempWorkerMutation';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyTempWorker } from '../../services/apiSLTP/dailyReports/dailyTempWorkers';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DraggableCell from '../dnd/DraggableCell';
import DroppableCell from '../dnd/DroppableCell';
import ManageDailyTempWorkerModal from '../modals/operations/planning/ManageDailyTempWorkerModal';
import IconButton from '../ui/IconButton';
import { DND_ITEM_TYPES } from '../../constants/dnd';

interface TempWorkersCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
}

const TempWorkersCell: React.FC<TempWorkersCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
}) => {
    const { t } = useTranslation();
    const itemType = DND_ITEM_TYPES.TEMPWORKERS;

    const { mutate: updateDailyTempWorker } = useDailyTempWorkerMutation({ onSuccess: refetch });
    const { mutate: deleteDailyTempWorker } = useDeleteDailyTempWorkerMutation({ onSuccess: refetch });
    const { mutate: createDailyTempWorker } = useCreateDailyTempWorkerMutation({ onSuccess: refetch });

    const { selectedItem, manageModal, handleMove, handleDelete, handleDuplicate, openModal } = useDailyResource<DailyTempWorker>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailyTempWorker({ id: itemId, body: { activityId: toLineId } });
        },
        onDelete: async (itemId) => {
            deleteDailyTempWorker({ id: itemId });
        },
        onDuplicate: async (tempWorker) => {
            createDailyTempWorker({
                body: {
                    activityId: activity._id,
                    tempWorkerTypeId: tempWorker.tempWorkerTypeId._id,
                    durationHours: tempWorker.durationHours,
                    tempWorkerName: tempWorker.tempWorkerName,
                    includedInExpenses: tempWorker.includedInExpenses,
                    price: tempWorker.price,
                    priceType: tempWorker.priceType,
                },
            });
        },
    });

    const showCheckbox = activity.stepMode === stepModeEnum.VALIDATE;

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1, color: 'black' }}>
            <DroppableCell dropId={{ lineId: activity._id }} moveCell={handleMove} itemType={itemType} disabled={disabled}>
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => openModal(null)}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addTempWorker'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailyTempWorkers.map((tempWorker, index) => (
                            <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                <DraggableCell
                                    dragIds={{ itemId: tempWorker._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDelete(tempWorker._id)}
                                    disabled={disabled}
                                    state={tempWorker.state}
                                    onDuplicate={() => handleDuplicate(tempWorker)}
                                    onDoubleClick={() => openModal(tempWorker)}
                                >
                                    <div
                                        title={tempWorker.durationHours + t('hourAbreviation') + ' - ' + tempWorker.tempWorkerTypeId.designation}
                                        className="one-line-container"
                                    >
                                        <div className="font-w-700 one-line">
                                            {tempWorker.tempWorkerTypeId.designation}
                                            {tempWorker.durationHours && ' - ' + tempWorker.durationHours + t('hourAbreviation')}
                                        </div>
                                        {activity.stepMode === stepModeEnum.VALIDATE && (
                                            <div className="one-line" title={tempWorker.tempWorkerTypeId.codeProC}>
                                                {tempWorker.tempWorkerTypeId.codeProC}
                                            </div>
                                        )}
                                        <div className="one-line">{tempWorker.tempWorkerName}</div>
                                    </div>
                                </DraggableCell>

                                {showCheckbox && (
                                    <div className="h-25px fx-r fx-items-c" title={capitalizeFirstLetter(t('includedInExpenses'))}>
                                        <Checkbox
                                            checked={tempWorker.includedInExpenses}
                                            onChange={(_, checked) => {
                                                updateDailyTempWorker(
                                                    { id: tempWorker._id, body: { includedInExpenses: checked } },
                                                    {
                                                        onSuccess: () => {
                                                            refetch();
                                                        },
                                                    }
                                                );
                                            }}
                                            size="small"
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>

            {manageModal.value && (
                <ManageDailyTempWorkerModal
                    open={manageModal.value}
                    activity={activity}
                    handleClose={() => manageModal.setFalse()}
                    refetch={refetch}
                    dailyTempWorker={selectedItem}
                />
            )}
        </Box>
    );
};

export default TempWorkersCell;
